import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { ReactComponent as RightArrowSVG } from '../../assets/svgs/right-arrow.svg';
import Aos from 'aos';
import 'aos/dist/aos.css';
import { useNavigate } from 'react-router-dom';


const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 150px;

  img {
    height: 500px;
    width: 100%;
  }

  @media (max-width: 768px) {
    margin-bottom: 80px;
  }
`;

const Title = styled.div`
  font-size: 40px;
  display: flex;
  color: #385963;
  font-weight: 600;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 25px;
    margin-bottom: 20px;
    place-self: center;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 20px;
    place-self: center;
  }
`;

const Description = styled.div`
  font-size: 20px;
  margin-bottom: 20px;
  color: #91abb3;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: left;
`;

const ControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const CarouselContainer = styled.div`
  position: relative;
  max-width: 100%;
  overflow: hidden;
  margin-top: 20px;

  @media (max-width: 768px) {
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;

    /* hide scrollbar */
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const Slider = styled.div`
  display: flex;
  transition: transform 0.6s ease;

  @media (max-width: 768px) {
    transition: none; /* disable JS-based shifting on mobile */
    width: auto;
  }
`;

const Slide = styled.div`
  width: 300px;
  height: 300px;
  position: relative;
  flex-shrink: 0;
  border-radius: 20px;
  margin-right: 30px;
  overflow: hidden;
  transition: transform 0.5s ease;

  scroll-snap-align: start; /* snap each card cleanly */

  &:hover {
    transform: translateY(-5px);
  }
`;


// const CarouselContainer = styled.div`
//   position: relative;
//   max-width: 100%;
//   overflow: hidden;
//   margin-top: 20px;
// `;

// const Slider = styled.div`
//   display: flex;
//   transition: transform 0.6s ease;
// `;

// const Slide = styled.div`
//   width: 300px;
//   height: 300px;
//   position: relative;
//   flex-shrink: 0;
//   border-radius: 20px;
//   margin-right: 30px;
//   overflow: hidden;
//   transition: transform 0.5s ease;
//   // filter: ${({ isVisible }) => (isVisible ? 'none' : 'blur(1px)')};

//   &:hover {
//     transform: translateY(-5px);
//   }
// `;

const SlideImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 20px;
  transition: transform 0.8s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const Overlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  color: white;
  background: rgba(0, 0, 0, 0.29);
  padding: 10px;
  border-radius: 5px;
  text-align: left;
  width: 100%;
  height: 100%;
  // align-items:center;

  h3 {
    font-size: 18px;
    font-weight: 600;
    position: absolute;
    bottom: 10px;
    left: 10px;
  }
`;

const Button = styled.button`
  background: transparent;
  border: none;
  padding: 10px 15px;
  cursor: pointer;
  border-radius: 50%;
  font-size: 24px;
  transition: all 0.3s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const FILE_PLACE_TYPE = {
  Gallery: 0,
  CategoryPhoto: 1,
  CoverPhoto: 2,
  Poster: 3,
};

const PopularDestinationsHome = () => {
  const containerRef = useRef(null);
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const [activeLink, setActiveLink] = useState('/');
  const [destinations, setDestinations] = useState([]);
  const slideWidth = 300;
  const slideMarginRight = 30;
  const totalSlideWidth = slideWidth + slideMarginRight;
  const [containerWidth, setContainerWidth] = useState(0);
  const [visibleSlidesCount, setVisibleSlidesCount] = useState(1);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    Aos.init({ duration: 1000 });
  }, []);

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const response = await fetch(`${BASE_URL}Destination/getTop10`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            takeLastYearAsCalculationPeriod: true,
          }),
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();

        const rawFileNames = data.flatMap((d) => d.fileNames || []);
        const coverPhotoFileNames = rawFileNames.filter((name) => name.startsWith('CoverPhoto_'));

        let fetchedFiles = [];
        if (coverPhotoFileNames.length > 0) {
          const fileResponse = await fetch(`${BASE_URL}Destination/getFiles`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(coverPhotoFileNames),
          });

          if (!fileResponse.ok) throw new Error('Failed to fetch file contents');
          fetchedFiles = await fileResponse.json();
        }

        const updatedDestinations = data.map((dest) => {
          const coverFile = fetchedFiles.find(
            (f) => f.filePlaceType === FILE_PLACE_TYPE.CoverPhoto && dest.fileNames?.includes(f.fileName)
          );

          const coverBase64Src = coverFile
            ? `data:${coverFile.mimeType};base64,${coverFile.base64Content}`
            : null;

          return {
            ...dest,
            coverBase64Src,
          };
        });

        setDestinations(updatedDestinations);
      } catch (error) {
        console.error('Failed to fetch destinations:', error);
      }
    };

    fetchDestinations();
  }, [BASE_URL]);

  useEffect(() => {
  const updateWidth = () => {
    if (!containerRef.current || destinations.length === 0) return;

    const width = containerRef.current.offsetWidth;
    setContainerWidth(width);

    const count = Math.floor(width / totalSlideWidth) || 1;
    setVisibleSlidesCount(count);

    const maxIndex = Math.max(destinations.length - count, 0);

    setCurrentIndex((current) => Math.min(current, maxIndex));
  };

  updateWidth();
  window.addEventListener('resize', updateWidth);
  return () => window.removeEventListener('resize', updateWidth);
}, [destinations.length, totalSlideWidth]);


  // Calculate container width & visible slides on mount and resize
  // useEffect(() => {
  //   const updateWidth = () => {
  //     if (containerRef.current) {
  //       const width = containerRef.current.offsetWidth;
  //       setContainerWidth(width);
  //       const count = Math.floor(width / totalSlideWidth) || 1;
  //       setVisibleSlidesCount(count);
  //       // Ensure currentIndex stays valid if visibleSlidesCount changes
  //       setCurrentIndex((current) => Math.min(current, destinations.length - count));
  //     }
  //   };

  //   updateWidth();
  //   window.addEventListener('resize', updateWidth);
  //   return () => window.removeEventListener('resize', updateWidth);
  // }, [destinations.length, totalSlideWidth]);

  const isVisible = (index) => {
    return index >= currentIndex && index < currentIndex + visibleSlidesCount;
  };

  const nextSlide = () => {
    if (currentIndex < destinations.length - visibleSlidesCount) {
      setCurrentIndex((prevIndex) => prevIndex + 1);
    }
  };

  const prevSlide = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prevIndex) => prevIndex - 1);
    }
  };

  const handleSlideClick = (destinationId) => {
  navigate('/destinations', {
    state: {
      activeComponent: 'DestinationTemplate',
      destinationId,
      category: 'destination',
    },
  });
};


  const MoreButton = styled(Link)`
    // background-color: white;
    background-color: #385963;
    color: white;
    // font-weight: 400;
    // border: #385963 solid 2px;
    // width: ${currentLanguage === 'en' ? '200px' : '300px'};
    height: 20px;
    padding: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    // border-radius: 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-decoration: none;
    margin-top: 10px;
    font-weight: 600;
    padding: 14px 28px;
    border-radius: 9999px;
    border: none;
    cursor: pointer;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
    transition: background 0.2s ease;
    min-width: 200px;
    height: 80px;

    svg {
      margin-left: 10px;
    }

    &:hover {
    background: #91abb3;
  }


    // &:hover {
    //   box-shadow: rgba(0, 0, 0, 0.6) 0px 10px 50px;
    //   color: white;
    //   cursor: pointer;
    //   background-color: #385963;
    // }

    @media (max-width: 768px) {
      font-size: 12px;
      padding: 12px 24px;
      // padding: 20px 5px;
      min-width: 160px;
      height: 60px;
      // width: ${currentLanguage === 'en' ? '150px' : '190px'};
    }

    @media (max-width: 480px) {
      font-size: 12px;
      padding: 12px 24px;
      min-width: 160px;
      height: 60px;
    }
  `;


  return (
    <Container>
      <Title data-aos="zoom-in-down">{i18n.t('popularDestinations')}</Title>
      <ControlsContainer>
        <MoreButton to="/destinations" onClick={() => setActiveLink('/destinations')}>
          {i18n.t('discoverMore')}
          {/* <RightArrowSVG height={18} wisdth={18} /> */}
        </MoreButton>

        <div data-aos="fade-left">
          <Button onClick={prevSlide} disabled={currentIndex === 0}>
            &#10094;
          </Button>{' '}
          <Button onClick={nextSlide} disabled={currentIndex >= destinations.length - visibleSlidesCount}>
            &#10095;
          </Button>{' '}
        </div>
      </ControlsContainer>

      <CarouselContainer ref={containerRef} data-aos="fade-right">
        <Slider
          style={{
            transform: `translateX(-${currentIndex * totalSlideWidth}px)`,
            width: `${destinations.length * totalSlideWidth}px`,
          }}
        >
          {destinations.map((destination, index) => (
            <Slide key={index} isVisible={isVisible(index)} onClick={() => handleSlideClick(destination.id)}>
              <a className="GridTeaser">
                <SlideImage
                  src={destination.coverBase64Src || 'fallback-image.jpg'}
                  alt={destination.name[currentLanguage] || destination.name.en}
                />
                <Overlay>
                  <h3>{destination.name[currentLanguage] || destination.name.en}</h3>
                </Overlay>
              </a>
            </Slide>

          ))}
        </Slider>
      </CarouselContainer>
    </Container>
  );
};

export default PopularDestinationsHome;
